"use client";
import WarningDisplay from "@/components/Nav/WarningDisplay";
import OnboardingGuard from "@/components/OnboardingGuard";
import { GetViewAs, RemoveViewAs } from "@/services/Cookies/cookies";
import { useAuth } from "@/services/Firebase/Authcontext";
import {
	AdsClick,
	Assignment,
	Business,
	BusinessCenter,
	Calculate,
	Dashboard,
	DatasetLinkedOutlined,
	Description,
	EditNote,
	Forum,
	Grading,
	PersonAddAlt1,
	PersonSearch,
	PictureAsPdf,
	PostAdd,
	QueryStats,
	School,
	Search,
	StarOutline,
	TravelExplore,
} from "@mui/icons-material";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import { Box } from "@mui/material";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { DialogsProvider, type Navigation } from "@toolpad/core";
import { NextAppProvider } from "@toolpad/core/nextjs";
import { NotificationsProvider } from "@toolpad/core/useNotifications";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
// import AD4 from "../GoogleAds/ad4";
import { CustomSession } from "@/components/Nav/Session";
import { IWarningResponse, ISystemAlert } from "@/types/WarningTypes";
import GradingIcon from "@mui/icons-material/Grading";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ErrorBoundary } from "../ErrorBoundary";
import { MiniFooter } from "@/components/Footer/Footer";
import { SidebarLayout } from "@/components/Nav/SidebarLayout";
import { GetRefreshToken, GetSessionCookie } from "@/services/Cookies/FirebaseSession";
import { isTokenExpired, isTokenExpiringSoon, getTimeUntilExpiry } from "@/utils/jwtUtils";

export const lightColors = {
	primary: {
		main: "#3258a3",
		light: "rgb(91, 121, 181)",
		dark: "rgb(35, 61, 114)",
	},
	secondary: {
		main: "#a33258",
		light: "rgb(181, 91, 121)",
		dark: "rgb(114, 35, 61)",
		contrastText: "#eee",
	},
};

export const darkColors = {
	primary: {
		main: "#90caf9",
		light: "#e3f2fd",
		dark: "#42a5f5",
	},
	secondary: {
		main: "#ce93d8",
		light: "#f3e5f5",
		dark: "#f3e5f5",
	},
};

const lightThemeBase = createTheme({
	cssVariables: true,
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1900,
		},
	},
	palette: {
		mode: "light",
		background: {
			default: "#f4f6f8",
			paper: "#fff",
		},
		primary: {
			main: lightColors.primary.main,
			light: lightColors.primary.light,
			dark: lightColors.primary.dark,
		},
		secondary: {
			main: lightColors.secondary.main,
			light: lightColors.secondary.light,
			dark: lightColors.secondary.dark,
			contrastText: lightColors.secondary.contrastText,
		},
	},
	shape: {
		borderRadius: 10,
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: "none",
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					borderRadius: 0,
					background: `linear-gradient(0deg, ${lightColors.primary.dark} 0%, ${lightColors.primary.main} 100%) !important`,
					"& .MuiSvgIcon-root": {
						color: "#fff",
					},
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				docked: {
					"& .MuiDrawer-paper": {
						borderRadius: 0,
					},
					"& .MuiListItemButton-root": {
						paddingTop: "2px",
						paddingBottom: "2px",
					},
					"& .MuiListItemText-primary": {
						fontSize: "0.9rem",
					},
					"& nav": {
						"::-webkit-scrollbar": {
							display: "none",
						},
						msOverflowStyle: "none",
						scrollbarWidth: "none",
					},
				},
			},
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					"&.Mui-selected": {
						background: `linear-gradient(90deg, ${lightColors.primary.dark} 0%, ${lightColors.primary.light} 100%)`,
						"& .MuiTypography-root": {
							color: "#fff !important",
							fontWeight: "bold",
						},
						"& .MuiSvgIcon-root": {
							color: "#fff !important",
						},
					},
				},
			},
		},
		MuiListSubheader: {
			styleOverrides: {
				sticky: {
					position: "relative",
					color: lightColors.primary.main,
					fontWeight: "bold",
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"&:nth-child(odd)": {
						backgroundColor: "#E7E9EB",
					},
				},
				head: {
					background: `linear-gradient(0deg, ${lightColors.primary.dark} 0%, ${lightColors.primary.light} 100%) !important`,
					"& .MuiTableCell-head": {
						color: "#fff",
						fontWeight: "bold",
					},
				},
			},
		},
		MuiTableSortLabel: {
			styleOverrides: {
				root: {
					"&:hover": {
						color: "rgba(255, 255, 255, 0.67)",
					},
					"&.Mui-active": {
						color: "#fff",
						fontWeight: "bold",
						"& .MuiSvgIcon-root": {
							color: "#fff",
						},
						"&:hover": {
							color: "rgba(255, 255, 255, 0.67)",
						},
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					"&.Mui-selected": {
						background: `linear-gradient(90deg, ${lightColors.primary.dark} 0%, ${lightColors.primary.light} 100%)`,
						color: "#fff",
						fontWeight: "bold",
						"& .MuiSvgIcon-root": {
							color: "#fff !important",
						},
					},
				},
			},
		},
	},
});

export const lightTheme = responsiveFontSizes(lightThemeBase);

const darkThemeBase = createTheme({
	cssVariables: true,
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1900,
		},
	},
	palette: {
		mode: "dark",
		background: {
			default: "#121212",
			paper: "#1e1e1e",
		},
		primary: {
			main: darkColors.primary.main,
			light: darkColors.primary.light,
			dark: darkColors.primary.dark,
		},
		secondary: {
			main: darkColors.secondary.main,
			light: darkColors.secondary.light,
			dark: darkColors.secondary.dark,
		},
	},
	components: {
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: "10px",
				},
			},
		},
		MuiContainer: {
			styleOverrides: {
				root: {
					borderRadius: "10px",
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: "10px",
					textTransform: "none",
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: "10px",
				},
			},
		},
		MuiCardActionArea: {
			styleOverrides: {
				root: {
					borderRadius: "10px",
				},
			},
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					borderRadius: "10px",
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					borderRadius: 0,
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				docked: {
					"& .MuiDrawer-paper": {
						borderRadius: 0,
						backgroundColor: "#1a1a1a",
					},
					"& .MuiListItemButton-root": {
						paddingTop: "2px",
						paddingBottom: "2px",
						color: "#e0e0e0",
						"&:hover": {
							backgroundColor: "rgba(144, 202, 249, 0.08)",
						},
					},
					"& .MuiListItemText-primary": {
						fontSize: "0.9rem",
						color: "#e0e0e0",
					},
					"& nav": {
						"::-webkit-scrollbar": {
							display: "none",
						},
						msOverflowStyle: "none",
						scrollbarWidth: "none",
					},
				},
			},
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					color: "#e0e0e0",
					"&.Mui-selected": {
						background: `linear-gradient(90deg, ${darkColors.primary.dark} 0%, ${darkColors.primary.main} 100%)`,
						"& .MuiTypography-root": {
							color: "#fff !important",
							fontWeight: "bold",
						},
						"& .MuiSvgIcon-root": {
							color: "#fff !important",
						},
					},
				},
			},
		},
		MuiListSubheader: {
			styleOverrides: {
				sticky: {
					position: "relative",
					color: darkColors.primary.main,
					backgroundColor: "#1a1a1a",
					fontWeight: "bold",
				},
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					color: "#e0e0e0",
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"&:nth-child(odd)": {
						backgroundColor: "#232323",
					},
				},
				head: {
					background: "#1a1a1a !important",
					"& .MuiTableCell-head": {
						color: "#e0e0e0",
						fontWeight: "bold",
					},
				},
			},
		},
		MuiTableSortLabel: {
			styleOverrides: {
				root: {
					color: "#e0e0e0",
					"&:hover": {
						color: "rgba(144, 202, 249, 0.87)",
					},
					"&.Mui-active": {
						color: darkColors.primary.main,
						fontWeight: "bold",
						"& .MuiSvgIcon-root": {
							color: darkColors.primary.main,
						},
						"&:hover": {
							color: "rgba(144, 202, 249, 0.87)",
						},
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					color: "#e0e0e0",
					"&.Mui-selected": {
						background: `linear-gradient(90deg, ${darkColors.primary.dark} 0%, ${darkColors.primary.main} 100%)`,
						color: "#fff",
						fontWeight: "bold",
						"& .MuiSvgIcon-root": {
							color: "#fff !important",
						},
					},
				},
			},
		},
	},
});

export const darkTheme = responsiveFontSizes(darkThemeBase);

interface NavbarProps {
	warnings: IWarningResponse[];
	children: React.ReactNode;
}

export default function Navbar({ children, warnings }: NavbarProps) {
	const { sessionUser: user, subscription, currentUser, refreshCookie } = useAuth();
	const PRODUCT_PREFERENCES = { professional: true, student: false } as const;
	const [preferences, setPreferences] = useState<{
		professional: boolean;
		student: boolean;
	}>(PRODUCT_PREFERENCES);
	const [viewAsEnabled, setViewAsEnabled] = useState<boolean>(false);

	const [loadingRefreshToken, setLoadingRefreshToken] = useState<boolean>(true);
	const [hasRefreshToken, setHasRefreshToken] = useState<boolean>(false);
	const [sessionExpired, setSessionExpired] = useState<boolean>(false);
	const [sessionExpiringSoon, setSessionExpiringSoon] = useState<boolean>(false);
	const [timeUntilExpiry, setTimeUntilExpiry] = useState<{
		minutes: number;
		seconds: number;
	} | null>(null);
	const [refreshingToken, setRefreshingToken] = useState<boolean>(false);

	const NAVIGATION: Navigation = [
		{
			segment: "dashboard",
			title: "Dashboard",
			icon: <Dashboard />,
		},
		{
			segment: "resume-builder",
			title: "Resume Builder",
			icon: <Description />,
			pattern: "resume-builder{/:uuid}*",
		},
		{
			segment: "motivation-letter-creator",
			title: "Cover Letter",
			icon: <Grading />,
		},

		// Professional section
		...(preferences.professional
			? ([
				{
					kind: "divider",
				},
				{
					kind: "header",
					title: "For Professionals",
				},
				{
					segment: "jobtracker",
					title: "Job Tracker",
					icon: <BusinessCenter />,
				},
				{
					segment: "extension",
					title: "Extension",
					icon: <AdsClick />,
				},
				{
					segment: "jobs",
					title: "Job Insights",
					icon: <TravelExplore />,
				},
				{
					title: "Referral",
					segment: "referral",
					icon: <PersonAddAlt1 />,
					children: [
						{
							segment: "referral",
							title: "Browse Referrals",
							icon: <PersonSearch />,
							pattern: "referral",
						},
						{
							segment: "referral/applications",
							title: "My Applications",
							icon: <EditNote />,
							pattern: "referral/applications",
						},
						{
							segment: "referral/manage-postings",
							title: "My Postings",
							icon: <PostAdd />,
							pattern: "referral/manage-postings",
						},
					],
				},
				{
					segment: "company-status",
					title: "Track Company Status",
					icon: <QueryStats />,
				},
				{
					segment: "discussions",
					title: "Discussions",
					icon: <Forum />,
				},
				{
							segment: "linkedin-optimization",
							title: "LinkedIn Optimization",
							icon: <DatasetLinkedOutlined />,
						},
				{
					title: "Job Tools",
					segment: "job-tools",
					icon: <Business />,
					children: [
						{
							segment: "job-tools/hidden-job-finder",
							title: "Hidden Job Finder",
							icon: <Search />,
						},
						{
							segment: "job-tools/pdf-merge",
							title: "PDF Merge",
							icon: <PictureAsPdf />,
						},
						
					],
				},
			] as Navigation)
			: []),

		// // Student section
		// ...(preferences.student
		// 	? ([
		// 		{
		// 			kind: "divider",
		// 		},
		// 		{
		// 			kind: "header",
		// 			title: "Study in Germany",
		// 		},

		// 		{
		// 			segment: "letter-of-recommendation",
		// 			title: "Recommendation Letter",
		// 			icon: <HistoryEduIcon />,
		// 		},
		// 		{
		// 			segment: "student-processes",
		// 			title: "Process Tracker",
		// 			icon: <QueryStats />,
		// 		},
		// 		...(user?.uid
		// 			? [
		// 				{
		// 					segment: "application-tracker",
		// 					title: "Application Trackers",
		// 					icon: <Assignment />,
		// 				},
		// 			]
		// 			: []),
		// 		// {
		// 		// 	kind: "header",
		// 		// 	title: "Courses",
		// 		// },

		// 		// {
		// 		// 	segment: "courses",
		// 		// 	title: "Browse courses",
		// 		// 	icon: <School />,
		// 		// 	pattern: "courses{/:courseId}*",
		// 		// },

		// 		// {
		// 		// 	segment: "germany/university",
		// 		// 	title: "Browse Universities",
		// 		// 	icon: <Business />,
		// 		// 	pattern: "germany/university",
		// 		// },
		// 		// {
		// 		// 	segment: "shortlists",
		// 		// 	title: "Shortlisted Programs ",
		// 		// 	icon: <StarOutline />,
		// 		// },
		// 	] as Navigation)
		// 	: []),
	];

	const router = useRouter();
	const pathname = usePathname();

	const session: CustomSession = user?.uid
		? {
			user: {
				id: user.uid,
				name: user.displayName,
				email: user.email,
				image: user.photoURL,
				role: user?.role,
				subscription: subscription || null,
			},
		}
		: null;

	const stopViewAs = async () => {
		await RemoveViewAs();
		location.reload();
	};

	const checkSessionStatus = async () => {
		try {
			const sessionCookie = await GetSessionCookie();
			if (!sessionCookie) {
				setSessionExpired(false);
				setSessionExpiringSoon(false);
				setTimeUntilExpiry(null);
				return;
			}

			const expired = isTokenExpired(sessionCookie);
			const expiringSoon = isTokenExpiringSoon(sessionCookie, 60); // 5 minutes threshold
			const timeRemaining = getTimeUntilExpiry(sessionCookie);

			setSessionExpired(expired === true);
			setSessionExpiringSoon(expiringSoon === true && expired !== true);
			setTimeUntilExpiry(timeRemaining);
		} catch (error) {
			console.error("Error checking session status:", error);
		}
	};

	const handleRefreshToken = async () => {
		setRefreshingToken(true);
		try {
			let success = false;

			// Try using current user's ID token first
			if (currentUser) {
				success = await refreshCookie();
			}

			if (success) {
				// Check session status again after refresh
				setTimeout(() => {
					checkSessionStatus();
				}, 1000);
			} else {
				console.error("Failed to refresh token");
			}
		} catch (error) {
			console.error("Error refreshing token:", error);
		} finally {
			setRefreshingToken(false);
		}
	};

	const authentication = React.useMemo(() => {
		return {
			signIn: () => {
				router.push("/login");
			},
			signOut: () => {
				const params = new URLSearchParams(window.location.search);
				const fullPath = encodeURIComponent(pathname + "?" + params.toString());
				router.push(`/logout?redirect=${fullPath}`);
			},
		};
	}, []);

	useEffect(() => {
		const updatePreferences = () => {
			setPreferences(PRODUCT_PREFERENCES);
		};

		updatePreferences();

		// check if viewAs is enabled
		GetViewAs().then((value) => {
			if (value) setViewAsEnabled(true);
		});

		GetRefreshToken().then((token) => {
			if (token) {
				setHasRefreshToken(true);
			}
			setLoadingRefreshToken(false);
		});

		// add event listener for preference update
		window.addEventListener("userInfoUpdated", () => {
			updatePreferences();
		});

		// Initial session status check
		checkSessionStatus();

		// Set up interval to check session status every 30 seconds
		const sessionCheckInterval = setInterval(checkSessionStatus, 30000);

		// remove event listener on unmount
		return () => {
			window.removeEventListener("userInfoUpdated", () => {
				updatePreferences();
			});
			clearInterval(sessionCheckInterval);
		};
	}, []);

	useEffect(() => {
		const observer = new MutationObserver(() => {
			const affectedDivs = document.querySelectorAll("body > div");

			affectedDivs.forEach((div) => {
				(div as HTMLElement).style.height = "";
			});
		});

		observer.observe(document.body, {
			attributes: true,
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, []);

	// Generate system alerts based on current state
	const systemAlerts: ISystemAlert[] = [];

	// View as alert
	if (viewAsEnabled && user) {
		systemAlerts.push({
			id: "view-as-enabled",
			description: `Viewing as another user: ${user.email}`,
			severity: "info",
			buttonText: "Stop Viewing",
			buttonAction: stopViewAs,
			dismissable: false,
		});
	}

	// Session expiring soon alert
	if (!loadingRefreshToken && sessionExpiringSoon && currentUser) {
		systemAlerts.push({
			id: "session-expiring-soon",
			description: `Your session will expire in ${timeUntilExpiry?.minutes}m ${timeUntilExpiry?.seconds}s.${!currentUser ? " Please login to refresh your session." : ""
				}`,
			severity: "warning",
			buttonText: refreshingToken ? "Refreshing..." : "Refresh Session",
			buttonAction: refreshingToken ? undefined : handleRefreshToken,
			dismissable: false,
		});
	}

	// Session expired alert
	if (!loadingRefreshToken && sessionExpired) {
		systemAlerts.push({
			id: "session-expired",
			description: `Your session has expired.${currentUser ? " Click to refresh your session." : " Please login again."}`,
			severity: "error",
			buttonText: currentUser ? (refreshingToken ? "Refreshing..." : "Refresh Session") : "Login",
			buttonAction: currentUser ? (refreshingToken ? undefined : handleRefreshToken) : undefined,
			buttonHref: currentUser ? undefined : "/login",
			dismissable: false,
		});
	}

	// Legacy session expired alert
	if (!loadingRefreshToken && hasRefreshToken && !user?.uid && !sessionExpired) {
		systemAlerts.push({
			id: "legacy-session-expired",
			description: "Your session has expired. Please login.",
			severity: "warning",
			buttonText: "Login",
			buttonHref: "/login",
			dismissable: false,
		});
	}

	return (
		<NextAppProvider
			session={session}
			authentication={authentication}
			theme={{
				light: lightTheme,
				dark: lightTheme,
			}}
		>
			<DialogsProvider>
				<NotificationsProvider
					slotProps={{
						snackbar: {
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "right",
							},
							autoHideDuration: 5000,
						},
					}}
				>
					<NotificationProvider>
						{/* <GoogleBanner client="ca-pub-9361839743175728" slot="6864985429"/> */}
						<ErrorBoundary>
							<SidebarLayout navigation={NAVIGATION}>
								<WarningDisplay warnings={warnings} systemAlerts={systemAlerts} />

								<Box
									sx={{
										flexGrow: 1,
										p: 2,
										position: "relative",
										maxWidth: { xs: "100vw", sm: "none" },
										pb: { xs: 20, md: 15, lg: 10 },
									}}
								>
									<OnboardingGuard>{children}</OnboardingGuard>
								</Box>
								<MiniFooter />
							</SidebarLayout>
						</ErrorBoundary>
					</NotificationProvider>
				</NotificationsProvider>
			</DialogsProvider>
		</NextAppProvider>
	);
}
